import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Home from "@/pages/Home";
import Club from "@/pages/Club";
import Policy from "@/pages/Policy";
import Onboarding from "@/pages/Onboarding";
import Foundations from "@/pages/member/Foundations";
import Drops from "@/pages/member/Drops";
import Calls from "@/pages/member/Calls";
import Guides from "@/pages/member/Guides";
import BrandFoundations from "@/pages/member/BrandFoundations";
import MiniPlanPage, { MiniPlanThanks } from "@/pages/MiniPlanPage";
import PlanQuestionnaire from "@/pages/PlanQuestionnaire";
import BetaWrapup from "@/pages/BetaWrapup";
import TrialExit from "@/pages/TrialExit";
import PlanReview from "@/pages/PlanReview";
import GapDraft from "@/pages/GapDraft";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminReviews from "@/pages/admin/AdminReviews";
import AdminContent from "@/pages/admin/AdminContent";
import AdminCalls from "@/pages/admin/AdminCalls";
import AuthPage from "@/pages/AuthPage";
import AppShell from "@/components/AppShell";
import Dashboard from "@/pages/member/Dashboard";

const LibraryRedirect = () => {
  const { search } = useLocation();
  const qs = new URLSearchParams(search);
  qs.set("tab", "templates");
  return <Navigate to={`/app/guides?${qs.toString()}`} replace />;
};
import Ask from "@/pages/member/Ask";
import Playbook from "@/pages/member/Playbook";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminQueue from "@/pages/admin/AdminQueue";
import AdminKB from "@/pages/admin/AdminKB";
import AdminResources from "@/pages/admin/AdminResources";
import AdminMembers from "@/pages/admin/AdminMembers";
import AdminSite from "@/pages/admin/AdminSite";
import AdminCodes from "@/pages/admin/AdminCodes";
import AdminSurveys from "@/pages/admin/AdminSurveys";
import AdminAnnouncements from "@/pages/admin/AdminAnnouncements";
import AdminReports from "@/pages/admin/AdminReports";
import AdminInvites from "@/pages/admin/AdminInvites";
import JoinPage from "@/pages/JoinPage";
import Membership from "@/pages/member/Membership";
import Survey from "@/pages/member/Survey";
import PaymentSuccess, { PaymentCancel } from "@/pages/PaymentResult";

const Loading = () => (
  <div className="min-h-screen flex items-center justify-center text-muted-foreground font-mono text-xs tracking-widest">LOADING…</div>
);

const Protected = ({ admin = false, gate = false, children }) => {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (admin && user.role !== "admin") return <Navigate to="/app" replace />;
  if (gate && user.role !== "admin" && !user.onboarded) return <Navigate to="/onboarding" replace />;
  return children;
};

const HomeRedirect = () => {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === "admin" ? "/admin" : "/app"} replace />;
};

function App() {
  return (
    <div className="App grain">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/the-roots-club" element={<Club />} />
            <Route path="/mini-marketing-plan" element={<MiniPlanPage />} />
            <Route path="/mini-marketing-plan/thanks" element={<MiniPlanThanks />} />
            <Route path="/plan-questionnaire/:token" element={<PlanQuestionnaire />} />
            <Route path="/plan-review/:token" element={<PlanReview />} />
            <Route path="/beta-wrapup/:token" element={<BetaWrapup />} />
            <Route path="/trial-exit/:token" element={<TrialExit />} />
            <Route path="/gap-draft/:token" element={<GapDraft />} />
            <Route path="/terms" element={<Policy kind="terms" />} />
            <Route path="/privacy" element={<Policy kind="privacy" />} />
            <Route path="/cookies" element={<Policy kind="cookies" />} />
            <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />
            <Route path="/login" element={<AuthPage mode="login" />} />
            <Route path="/register" element={<AuthPage mode="register" />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/join/:slug" element={<JoinPage />} />
            <Route path="/home" element={<HomeRedirect />} />
            <Route path="/app" element={<Protected gate><AppShell /></Protected>}>
              <Route index element={<Dashboard />} />
              <Route path="library" element={<LibraryRedirect />} />
              <Route path="ask" element={<Ask />} />
              <Route path="playbook" element={<Playbook />} />
              <Route path="foundations" element={<Foundations />} />
              <Route path="brand-foundations" element={<BrandFoundations />} />
              <Route path="drops" element={<Drops />} />
              <Route path="calls" element={<Calls />} />
              <Route path="guides" element={<Guides />} />
              <Route path="membership" element={<Membership />} />
              <Route path="survey" element={<Survey />} />
            </Route>
            <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/admin" element={<Protected admin><AppShell admin /></Protected>}>
              <Route index element={<AdminOverview />} />
              <Route path="queue" element={<AdminQueue />} />
              <Route path="kb" element={<AdminKB />} />
              <Route path="resources" element={<AdminResources />} />
              <Route path="members" element={<AdminMembers />} />
              <Route path="site" element={<AdminSite />} />
              <Route path="discount-codes" element={<AdminCodes />} />
              <Route path="surveys" element={<AdminSurveys />} />
              <Route path="announcements" element={<AdminAnnouncements />} />
              <Route path="reports" element={<AdminReports />} />
              <Route path="invites" element={<AdminInvites />} />
              <Route path="analytics" element={<AdminAnalytics />} />
              <Route path="reviews" element={<AdminReviews />} />
              <Route path="content" element={<AdminContent />} />
              <Route path="calls" element={<AdminCalls />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster theme="light" position="top-right" />
      </AuthProvider>
    </div>
  );
}

export default App;
